import { useMemo } from "react";

import { PayoutSortBy, PayoutSortOrder, type RewardedLens } from "../../gql-generated/types";
import { makePayoutDateRange, makePreviousPayoutDateRange } from "../../lens-insights/converters";
import { usePayoutStatsLensList, usePayoutStatsSummary, useTimeRange } from "../../lens-insights/hooks";
import { getInsightsDiffPct } from "../../lens-insights/math";
import { GranularityInDays, LensGranularity } from "../../lens-insights/models";
import { clampLensPlusPayoutDateRange } from "../../lens-insights/utils/lensPlusDateRanges";
import {
    formatNullableNumber,
    formatSignedPctNumber,
    formatWithUSFormat,
} from "../../lens-stats/formatters/formatNumbers";
import { type PayoutDriverStats, type UsePayoutDriverStatsArgs } from "../model/PayoutDrivers";
import { formatMilliUsd } from "../utils/formatMilliUsd";

// TODO: drop the lensList aggregate path once the GQL service exposes audience
// + lensPlusScore on org-wide PayoutSummary. Today this caps at 500 lenses.
const REWARDED_LENS_LIST_PARAMS = {
    countryFilters: [],
    sortBy: PayoutSortBy.SortByTotalAmountMilliUsd,
    sortOrder: PayoutSortOrder.SortOrderDesc,
    limit: 500,
};

// Mirrors the server-side score formula (settled-only — estimated signups
// can still change and shouldn't move the score).
const SCORE_SIGNUP_WEIGHT = 100;

const computeDiff = (current: number, previous: number): string | null => {
    const pct = getInsightsDiffPct(current, previous);
    return pct ? formatSignedPctNumber(pct) : null;
};

// Returns null when no lens reports a value — preserves "unavailable" vs "zero".
const sumAudience = (lenses: readonly RewardedLens[]): number | null => {
    let total = 0;
    let hasAny = false;
    for (const lens of lenses) {
        const value = lens.totalSubscriberUniqueUsers;
        if (value !== null && value !== undefined) {
            total += value;
            hasAny = true;
        }
    }
    return hasAny ? total : null;
};

const sumSettledSignups = (lenses: readonly RewardedLens[]): number =>
    lenses.reduce((acc, lens) => acc + lens.lensPlusSignups.settled, 0);

const computeAggregateScore = (audience: number | null, settledSignups: number): number | null =>
    audience === null ? null : audience + settledSignups * SCORE_SIGNUP_WEIGHT;

export const usePayoutDriverStats = ({
    unlockableId,
    timeRange: explicitTimeRange,
    markedAsExclusiveAt,
    skip = false,
}: UsePayoutDriverStatsArgs = {}): PayoutDriverStats => {
    const { selectedTimeRange } = useTimeRange({ defaultRange: LensGranularity.Lifetime });
    const timeRange = explicitTimeRange ?? selectedTimeRange;

    const currentRange = clampLensPlusPayoutDateRange(makePayoutDateRange(timeRange), markedAsExclusiveAt);
    const previousRange = clampLensPlusPayoutDateRange(makePreviousPayoutDateRange(timeRange), markedAsExclusiveAt);
    const { startDate, endDate } = currentRange.range;
    const { startDate: prevStartDate, endDate: prevEndDate } = previousRange.range;

    const isLifetime = GranularityInDays[timeRange] === undefined;
    // Server populates audience + lensPlusScore on summary only when narrowed
    // to a single unlockableId; otherwise we sum per-lens fields from lensList.
    const useLensListAggregate = !unlockableId;

    const { data: summary, loading: summaryLoading } = usePayoutStatsSummary({
        startDate,
        endDate,
        countryFilters: [],
        unlockableId,
        skip: skip || !currentRange.isValid,
    });

    const { data: prevSummary, loading: prevSummaryLoading } = usePayoutStatsSummary({
        startDate: prevStartDate,
        endDate: prevEndDate,
        countryFilters: [],
        unlockableId,
        skip: skip || isLifetime || !previousRange.isValid,
    });

    // Skip the rewarded-lens-list aggregate fetch until summary confirms there
    // are payouts in the window. Most homepage visitors don't have Lens+
    // payouts; without this gate they'd pay a 500-row query for stats that
    // would resolve to "—" anyway.
    const hasPayoutsInWindow = !!summary && summary.totalPayoutCount > 0;
    const hasPrevPayouts = !!prevSummary && prevSummary.totalPayoutCount > 0;

    const lensList = usePayoutStatsLensList({
        startDate,
        endDate,
        ...REWARDED_LENS_LIST_PARAMS,
        skip: skip || !currentRange.isValid || !useLensListAggregate || !hasPayoutsInWindow,
    });

    const prevLensList = usePayoutStatsLensList({
        startDate: prevStartDate,
        endDate: prevEndDate,
        ...REWARDED_LENS_LIST_PARAMS,
        skip: skip || !useLensListAggregate || isLifetime || !previousRange.isValid || !hasPrevPayouts,
    });

    const loading =
        summaryLoading ||
        prevSummaryLoading ||
        (useLensListAggregate && hasPayoutsInWindow && lensList.loading) ||
        (useLensListAggregate && hasPrevPayouts && prevLensList.loading);

    return useMemo<PayoutDriverStats>(() => {
        const empty = { value: "-", diffValue: null };
        if (!summary) {
            return { audience: empty, subscriptionsEarned: empty, score: empty, payout: empty, loading };
        }

        let audienceRaw: number | null;
        let subsSettled: number;
        let scoreRaw: number | null;
        let prevAudienceRaw: number | null;
        let prevSubsSettled: number | null;
        let prevScoreRaw: number | null;

        if (useLensListAggregate) {
            audienceRaw = sumAudience(lensList.lenses);
            subsSettled = sumSettledSignups(lensList.lenses);
            scoreRaw = computeAggregateScore(audienceRaw, subsSettled);
            // While previous-window data is still in flight (or skipped), null
            // out the aggregates so we don't compute spurious diffs against 0.
            const prevReady = !isLifetime && !!prevSummary && !prevSummaryLoading && !prevLensList.loading;
            if (prevReady) {
                prevAudienceRaw = sumAudience(prevLensList.lenses);
                prevSubsSettled = sumSettledSignups(prevLensList.lenses);
                prevScoreRaw = computeAggregateScore(prevAudienceRaw, prevSubsSettled);
            } else {
                prevAudienceRaw = null;
                prevSubsSettled = null;
                prevScoreRaw = null;
            }
        } else {
            audienceRaw = summary.totalSubscriberUniqueUsers ?? null;
            subsSettled = summary.lensPlusSignups.settled;
            scoreRaw = summary.lensPlusScore ?? null;
            prevAudienceRaw = prevSummary?.totalSubscriberUniqueUsers ?? null;
            prevSubsSettled = prevSummary?.lensPlusSignups.settled ?? null;
            prevScoreRaw = prevSummary?.lensPlusScore ?? null;
        }

        const payout = summary.totalEarningsMilliUsd;

        const audienceDiff =
            audienceRaw !== null && prevAudienceRaw !== null ? computeDiff(audienceRaw, prevAudienceRaw) : null;
        const subsDiff = prevSubsSettled === null ? null : computeDiff(subsSettled, prevSubsSettled);
        const payoutDiff = prevSummary ? computeDiff(payout, prevSummary.totalEarningsMilliUsd) : null;
        const scoreDiff = scoreRaw !== null && prevScoreRaw !== null ? computeDiff(scoreRaw, prevScoreRaw) : null;

        return {
            audience: { value: formatNullableNumber(audienceRaw), diffValue: audienceDiff },
            subscriptionsEarned: { value: formatWithUSFormat(subsSettled), diffValue: subsDiff },
            score: { value: formatNullableNumber(scoreRaw), diffValue: scoreDiff },
            payout: { value: formatMilliUsd(payout), diffValue: payoutDiff },
            loading,
        };
    }, [
        summary,
        prevSummary,
        useLensListAggregate,
        lensList.lenses,
        prevLensList.lenses,
        prevSummaryLoading,
        prevLensList.loading,
        isLifetime,
        loading,
    ]);
};
